import { supabase } from '../../lib/supabase'

let _authSubscription = null   // module-level singleton — never duplicated
let _profileLoading   = false  // mutex — prevents concurrent _loadProfile calls

export const userSlice = (set, get) => ({
  user:      null,
  profile:   null,
  audit:     [],
  authReady: false,

  initAuth: async () => {
    // Only ever register one listener — unsubscribe previous if somehow called again
    if (_authSubscription) {
      _authSubscription.unsubscribe()
      _authSubscription = null
    }

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.user) await get()._loadProfile(session.user)
    } catch (e) {
      console.warn('[Mori] getSession failed:', e.message)
    }

    set({ authReady: true })

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT' || !session?.user) {
        set({ user: null, profile: null, audit: [] })
        return
      }
      // Only load profile on actual sign-in events, not token refreshes that already loaded
      if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
        set({ user: session.user })
        await get()._loadProfile(session.user)
      }
    })

    _authSubscription = subscription
  },

  _loadProfile: async (authUser) => {
    // Mutex — prevent stacked concurrent calls
    if (_profileLoading) return
    _profileLoading = true

    try {
      set({ user: authUser })

      // Touch last_session_at — silently ignore if table doesn't exist yet
      try {
        await supabase
          .from('profiles')
          .update({ last_session_at: new Date().toISOString() })
          .eq('id', authUser.id)
      } catch (_) { /* table may not exist during first setup */ }

      const [profileRes, auditRes] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', authUser.id).single(),
        supabase.from('life_audit').select('*').eq('user_id', authUser.id)
          .order('recorded_at', { ascending: false }),
      ])

      // Don't crash if tables don't exist — just leave profile null
      set({
        profile: profileRes.error ? null : (profileRes.data ?? null),
        audit:   auditRes.error   ? []   : (auditRes.data ?? []),
      })
    } catch (e) {
      console.warn('[Mori] _loadProfile error:', e.message)
    } finally {
      _profileLoading = false
    }
  },

  signIn: async (email, password) => {
    const res = await supabase.auth.signInWithPassword({ email, password })
    return res
  },

  signUp: async (email, password) => {
    const res = await supabase.auth.signUp({ email, password })
    return res
  },

  signOut: async () => {
    if (_authSubscription) {
      _authSubscription.unsubscribe()
      _authSubscription = null
    }
    await supabase.auth.signOut()
    set({ user: null, profile: null, audit: [] })
  },

  saveProfile: async (data) => {
    const uid = get().user?.id
    if (!uid) return { error: { message: 'Not authenticated' } }
    const payload = { id: uid, ...data, updated_at: new Date().toISOString() }
    const { data: saved, error } = await supabase
      .from('profiles').upsert(payload).select().single()
    if (!error) set({ profile: saved })
    return { data: saved, error }
  },

  finishOnboarding: async () => {
    const uid = get().user?.id
    if (!uid) return
    const { data } = await supabase
      .from('profiles')
      .update({ onboarding_completed_at: new Date().toISOString() })
      .eq('id', uid).select().single()
    if (data) set({ profile: data })
    return data
  },

  saveAudit: async (scores) => {
    const uid = get().user?.id
    if (!uid) return
    const rows = scores.map(({ domain, score }) => ({
      user_id: uid, domain, score, recorded_at: new Date().toISOString(),
    }))
    const { data } = await supabase.from('life_audit').insert(rows).select()
    if (data) set({ audit: data })
  },

  isOnboarded: () => !!get().profile?.onboarding_completed_at,
})
